import { Fragment, useState, type ReactNode } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ChevronDownIcon, PanelLeftIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { MediaFolder } from '@/entities/brand-media';

/**
 * Which folder the column below is showing, and what can be done to it.
 *
 * The rail already says where you are by which row is lit, but only while it is on screen -- and the
 * button that hides it lives here, so the name has to be readable without it. The ancestors are
 * printed above the name rather than beside it because the name is the answer and the trail is the
 * qualifier; two folders called "Social cuts" are told apart by the line above, which is exactly
 * where a glance that has already read the big text goes next.
 *
 * The trail is a breadcrumb, and each step of it goes back to that folder -- the way up out of a
 * subfolder when the rail is hidden, which is the state this header exists for.
 *
 * A rename is committed here rather than handed to the rail. The rail owns its own inline editing,
 * and reaching into it would mean an imperative handle for a field this component can simply draw
 * over its own title.
 */
export const FolderHeader = ({
  path,
  filters,
  railHidden,
  onToggleRail,
  onNavigate,
  onRename,
  onDelete,
}: {
  /** Root first, the folder being shown last. Empty is the whole library rather than a folder. */
  path: readonly MediaFolder[];
  /**
   * Whatever else is narrowing the column, drawn beside the folder name. The folder is only half
   * of what the grid below is answering, and the other half has nowhere else on this row to go.
   */
  filters?: ReactNode;
  railHidden: boolean;
  onToggleRail: () => void;
  /** Open one of the ancestors in the trail, the same as choosing its row in the rail. */
  onNavigate: (folder: MediaFolder) => void;
  onRename: (folder: MediaFolder, name: string) => void;
  /** The folder itself, matching the rail: the confirmation names it and counts its subtree. */
  onDelete: (folder: MediaFolder) => void;
}) => {
  const { t } = useTranslation('tools');
  const [renaming, setRenaming] = useState(false);

  const folder = path.at(-1);
  const ancestors = path.slice(0, -1);
  const railLabel = railHidden ? t('brandMedia.showRail') : t('brandMedia.hideRail');

  /** A rename to the same words, or to nothing, is not a rename: leave the folder as it is. */
  const commitRename = (value: string) => {
    const trimmed = value.trim();
    if (folder !== undefined && trimmed.length > 0 && trimmed !== folder.name) {
      onRename(folder, trimmed);
    }
    setRenaming(false);
  };

  return (
    // Its own provider rather than the app's: this header is shared code, mounted by more than one
    // host, and a tooltip whose provider is missing throws rather than degrading.
    <TooltipProvider>
      <div className="flex items-center gap-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon-lg"
              className="shrink-0"
              aria-label={railLabel}
              onClick={onToggleRail}
            >
              <PanelLeftIcon className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{railLabel}</TooltipContent>
        </Tooltip>

        <div className="flex min-w-0 flex-col">
          {/* The trail is named rather than left as the design system's default: the page header
              above is a breadcrumb too, and two landmarks called "breadcrumb" are a list a screen
              reader user cannot choose from. */}
          {ancestors.length > 0 && (
            <Breadcrumb aria-label={t('brandMedia.folderTrail')}>
              <BreadcrumbList className="flex-nowrap">
                {ancestors.map((ancestor, index) => (
                  <Fragment key={ancestor.id}>
                    {index > 0 && <BreadcrumbSeparator />}
                    <BreadcrumbItem className="min-w-0">
                      <BreadcrumbLink asChild>
                        <button
                          type="button"
                          className="truncate"
                          onClick={() => onNavigate(ancestor)}
                        >
                          {ancestor.name}
                        </button>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          )}

          {/* One wrapping row so the filters sit against the name rather than under it, and so
              they survive a rename, which replaces only the title beside them. The name and its
              menu stay glued in their own group: wrapping is for the chips, and a chevron that
              broke away from the word it acts on would point at nothing. */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            {renaming && folder !== undefined ? (
              <Input
                // Focused and selected through a ref rather than `autoFocus`: this field replaces a
                // title in response to a menu choice, so taking focus is the point of it.
                ref={(input) => input?.select()}
                size="sm"
                className="w-64"
                // The title it stands in for is gone while it is open, so without this the field is
                // announced as an unlabelled box holding a word.
                aria-label={t('brandMedia.folderName')}
                defaultValue={folder.name}
                onBlur={(event) => commitRename(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                  if (event.key === 'Escape') setRenaming(false);
                }}
              />
            ) : (
              <div className="flex min-w-0 items-center gap-1">
                {/* The same step the design system gives an empty-state title, so the two headings
                  this column can show -- the folder you opened, and the notice that it is empty --
                  are the same size when one replaces the other. */}
                <h2 className="truncate text-2xl font-medium">
                  {folder?.name ?? t('brandMedia.allMedia')}
                </h2>

                {/* Nothing to rename or delete when the whole library is showing, and a menu whose
                  every item is disabled says less than no menu at all. */}
                {folder !== undefined && (
                  <DropdownMenu>
                    {/* The menu's trigger stays outside the tooltip's for the reason `FolderMenu`
                      gives: in an `asChild` chain the inner trigger's `data-state` is the one that
                      reaches the button. */}
                    <Tooltip>
                      <DropdownMenuTrigger asChild>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="shrink-0 text-muted-foreground"
                            // Not the rail's "Actions for {name}": this one sits against the name it
                            // acts on, and there is one of it, so naming the folder again would only
                            // put the same words on the page twice.
                            aria-label={t('brandMedia.folderMenu')}
                          >
                            <ChevronDownIcon className="size-4" />
                          </Button>
                        </TooltipTrigger>
                      </DropdownMenuTrigger>
                      <TooltipContent>{t('brandMedia.folderMenu')}</TooltipContent>
                    </Tooltip>

                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => setRenaming(true)}>
                        <PencilIcon className="size-4" />
                        {t('brandMedia.rename')}
                      </DropdownMenuItem>

                      <DropdownMenuItem variant="destructive" onClick={() => onDelete(folder)}>
                        <Trash2Icon className="size-4" />
                        {t('brandMedia.deleteFolder')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )}

            {filters}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};
